import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Currency } from "@nh/shared";
import { FranchisePricingCard } from "./components/FranchisePricingCard";
import type { ScrollGlobeSection } from "./components/scrollGlobe.types";
import { FRANCHISE_CONTENT } from "./franchise.content";
import { franchisePrice } from "./franchise.pricing";
import { useFranchiseSettings } from "./useFranchiseSettings";

/**
 * Las cuatro secciones de la landing, armadas a partir del contenido.
 *
 * ── Por qué un hook y no JSX en la página ────────────────────────────
 * `ScrollGlobe` recibe las secciones como DATOS, no como hijos. Armar ese
 * arreglo dentro de la página la habría convertido en cien líneas de
 * objetos literales, que es justo lo que la regla de la casa —cero JSX
 * suelto en las páginas— viene a evitar. Aquí la página vuelve a ser
 * cuatro líneas y esto se puede probar por su cuenta.
 *
 * ── El orden es el argumento ─────────────────────────────────────────
 * Portada (qué es y cuánto cuesta), qué recibes, cuánto reparte, y la
 * tarjeta de compra. Es el orden del sistema actual y no es casual: el
 * precio aparece arriba como dato, no como pregunta, y abajo ya con todo
 * lo que se compra escrito al lado.
 */
export function useFranchiseSections(currency: Currency): ScrollGlobeSection[] {
  const { t } = useTranslation();
  const { prices } = useFranchiseSettings();
  const { text: price } = franchisePrice(currency, prices);
  const { content } = FRANCHISE_CONTENT;

  return useMemo(
    () => [
      {
        id: "franquicia",
        badge: t("Franquicia digital"),
        title: t("Franquicias Digitales"),
        subtitle: `${price} ${currency} ${t(content.costUnit)}`,
        description: t(content.costTagline),
        align: "left",
        actions: [
          /*
           * ESTE llamado sigue llevando a `/register`, y el de la tarjeta
           * del cierre —el que sí genera el cobro— está en
           * `FranchisePricingCard`. Que hagan cosas distintas es
           * deliberado: este está en la PORTADA, antes de que se haya
           * explicado qué se compra, cuánto reparte y qué incluye. Un
           * cobro ahí le pide la tarjeta a alguien que todavía no ha
           * leído el argumento; abrir una cuenta, no.
           *
           * Además cierra el hueco de atribución por el único camino que
           * hoy existe: quien llega al pago CON sesión iniciada llega a
           * Stripe con nombre, y ese pago se puede acreditar solo. Ver el
           * bloque del webhook en `contracts/checkout.ts`.
           */
          { label: t(content.ctaLabel), variant: "primary", to: "/register" },
          /*
           * La segunda acción del original va a `/dashboard/mayoristas`,
           * que en este sistema no existe: los paquetes viven en la
           * página pública `/paquetes`, con su categoría de negocio.
           *
           * Y por eso cambia la etiqueta. El original dice "Ver paquetes
           * mayoristas"; `/paquetes` abre en la pestaña de recompra
           * personal, así que prometer "mayoristas" sería mandar a otra
           * cosa de la que dice el botón — el error que `ShopCta` ya
           * documenta haber corregido en la tienda.
           */
          { label: t("Ver los paquetes"), variant: "secondary", to: "/paquetes" },
        ],
      },
      {
        id: "beneficios",
        badge: t(content.beneficiosTitle),
        title: t("Tu tienda, tu marca,"),
        subtitle: t("dentro de It's The Best"),
        description: t(
          "Una franquicia digital es tu propiedad dentro de la plataforma: tu dirección propia, tus precios, tus clientes y el respaldo de toda la operación de It's The Best.",
        ),
        align: "left",
        features: content.beneficios.map((benefit) => ({
          title: t(benefit.title),
          description: t(benefit.desc),
        })),
      },
      {
        id: "ventajas",
        badge: t(content.ventajasTitle),
        title: t("Compras más barato y cobras por tu red"),
        subtitle: t("siendo socio activo"),
        description: t(
          "La franquicia te da tu tienda y el mejor precio. Las comisiones las genera tu red con el plan de compensación: solo las cobra quien hace su recompra del mes.",
        ),
        align: "center",
        /*
         * La CIFRA es el título de la tarjeta y la explicación la
         * descripción, y no al revés: la cifra es lo que se viene
         * buscando en esta sección, así que es lo que se lee primero.
         */
        features: content.ventajas.map((row) => ({
          title: row.pct,
          description: t(row.label),
        })),
      },
      {
        id: "adquirir",
        badge: t(content.costLabel),
        title: price,
        align: "center",
        content: <FranchisePricingCard currency={currency} />,
      },
    ],
    [t, price, currency, content],
  );
}
